import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import http from '../../../../shared/http'
import { TApi, TRoles } from '../../../../shared/const'
import { blobTypes, downloadFile, stateLabel } from '../../../../shared/helpers'
import { formatEuro } from '../../../../shared/courses'
import useDebouncedValue from '../../../../shared/useDebouncedValue'

import Btn from '../../../../UI/Btn'
import BtnFilter from '../../../../UI/BtnFilter'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import { companyTypeField, stateField } from '../../../../UI/FilterPanel/fields'
import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'
import Search from '../../../../UI/Search'
import Table from '../../../../UI/Table'

import CompanyDialog from './CompanyDialog'
import { filterCompanies, STAGE_OPTIONS } from './filter'

import styles from './style.module.scss'

/**
 * Auswertung der Kurse: wer begonnen und abgeschlossen hat, was an
 * Gutschrift entstanden ist — und wo der Kurs selbst hakt.
 *
 * Die Zahlen rechnet der Server; Landesadmins bekommen nur ihr Bundesland.
 * Die Kacheln oben sind Kurzwahl auf den Filter „Stand" der Liste — dieselbe
 * Einschränkung wie in der Filterleiste, kein zweiter Zustand.
 */
function CourseReport(props) {
	const { t } = useTranslation()
	const [kursId, setKursId] = useState('')
	const [schuljahr, setSchuljahr] = useState('')
	const [data, setData] = useState(null)
	const [laedt, setLaedt] = useState(true)
	const [suche, setSuche] = useState('')
	const [filterOffen, setFilterOffen] = useState(false)
	const [offen, setOffen] = useState(null)
	const [exportiert, setExportiert] = useState(false)
	const gesucht = useDebouncedValue(suche)

	const stageField = useMemo(
		() => ({ key: 'stage', label: t('Status'), options: STAGE_OPTIONS.map(o => ({ ...o, label: t(o.label) })) }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	const fields = useMemo(
		() => [stageField, ...(props.role === TRoles.ADMIN ? [] : [stateField(t)]), companyTypeField(t)],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[props.role, stageField]
	)
	const filters = useFilters({ storageKey: 'courseReport', fields })

	useEffect(() => {
		setLaedt(true)
		http.get(TApi.ADMIN_COURSE_REPORT, { params: { courseId: kursId || undefined, schoolYear: schuljahr || undefined } })
			.then(({ data: antwort }) => {
				setData(antwort)
				if (!kursId && antwort?.courses?.[0]) setKursId(String(antwort.courses[0].id))
				if (!schuljahr && antwort?.schoolYears?.[0]) setSchuljahr(antwort.schoolYears[0])
			})
			.catch(() => setData(null))
			.finally(() => setLaedt(false))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kursId, schuljahr])

	const zeilen = useMemo(
		() => filterCompanies(data?.companies || [], filters.query, gesucht),
		[data, filters.query, gesucht]
	)

	const kachel = stufe => {
		const aktiv = (filters.query.stage || []).includes(stufe)
		filters.setField('stage', aktiv ? [] : [STAGE_OPTIONS.find(o => o.value === stufe)].map(o => ({ ...o, label: t(o.label) })))
	}
	const istAktiv = stufe => (filters.query.stage || []).includes(stufe)

	const exportieren = () => {
		setExportiert(true)
		http.get(TApi.ADMIN_COURSE_REPORT_EXPORT, {
			params: { courseId: kursId || undefined, schoolYear: schuljahr || undefined },
			responseType: 'blob'
		})
			.then(({ data: datei }) => downloadFile(`Kurs-Auswertung-${schuljahr || ''}.xlsx`, new Blob([datei], { type: blobTypes.XLS })))
			.finally(() => setExportiert(false))
	}

	const k = data?.kpis || {}

	const spalten = useMemo(
		() => [
			{ Header: t('Junior Company'), accessor: 'name', width: 200 },
			{ Header: t('Type'), accessor: 'companyType', width: 70 },
			{ Header: t('state'), accessor: 'state', width: 70, Cell: ({ value }) => (value ? stateLabel(value) : '—') },
			{ Header: t('School'), accessor: 'schoolName', width: 180 },
			{ Header: t('Members'), accessor: 'members', width: 80, align: 'center' },
			{ Header: t('Started'), accessor: 'started', width: 80, align: 'center' },
			{ Header: t('Completed'), accessor: 'completed', width: 90, align: 'center' },
			{
				Header: t('Credit earned'),
				accessor: 'creditPending',
				width: 110,
				align: 'right',
				Cell: ({ row }) => formatEuro(Number(row.original.creditPending || 0) + Number(row.original.creditApplied || 0))
			},
			{
				Header: t('On invoice'),
				accessor: 'creditApplied',
				width: 110,
				align: 'right',
				Cell: ({ value }) => (Number(value) > 0 ? formatEuro(value) : '—')
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.miniHeader} title={t('Course evaluation')} bg="#f8faff">
				<div className={styles.headActions}>
					<Search
						value={suche}
						placeholder={t('Junior Company or school')}
						onChange={e => setSuche(e.target.value)}
						onSubmit={e => e.preventDefault()}
					/>
					<BtnFilter icon="arrow" onClick={() => setFilterOffen(true)} count={filters.count}>
						{t('Filter')} ({filters.count})
					</BtnFilter>
				</div>
			</MiniHeader>

			<div className={styles.selects}>
				<label>
					{t('Course')}
					<select value={kursId} onChange={e => setKursId(e.target.value)}>
						{(data?.courses || []).map(kurs => (
							<option key={kurs.id} value={kurs.id}>
								{kurs.title}
							</option>
						))}
					</select>
				</label>
				<label>
					{t('School year')}
					<select value={schuljahr} onChange={e => setSchuljahr(e.target.value)}>
						{(data?.schoolYears || []).map(jahr => (
							<option key={jahr} value={jahr}>
								{jahr}
							</option>
						))}
					</select>
				</label>
				<Btn theme="download" icon="download" iconSize={{ w: 24, h: 24 }} onClick={exportieren} disabled={exportiert}>
					XLS
				</Btn>
			</div>

			{laedt && <Loading />}

			{data && (
				<>
					<div className={styles.tiles}>
						<div className={styles.tile}>
							<span className={styles.tileLabel}>{t('Students completed')}</span>
							<span className={styles.tileValue}>{k.studentsCompleted ?? 0}</span>
							<span className={styles.tileHint}>
								{t('{{started}} started · {{eligible}} eligible', {
									started: k.studentsStarted ?? 0,
									eligible: k.studentsEligible ?? 0
								})}
							</span>
						</div>
						<button
							type="button"
							className={clsx(styles.tile, styles.tileButton, istAktiv('withCompletion') && styles.tileActive)}
							onClick={() => kachel('withCompletion')}
						>
							<span className={styles.tileLabel}>{t('Companies with a completion')}</span>
							<span className={styles.tileValue}>{k.companiesWithCompletion ?? 0}</span>
							<span className={styles.tileHint}>
								{t('of {{n}} eligible', { n: k.companiesEligible ?? 0 })}
							</span>
						</button>
						<button
							type="button"
							className={clsx(styles.tile, styles.tileButton, istAktiv('allDone') && styles.tileActive)}
							onClick={() => kachel('allDone')}
						>
							<span className={styles.tileLabel}>{t('All members completed')}</span>
							<span className={styles.tileValue}>{k.companiesAllDone ?? 0}</span>
						</button>
						<button
							type="button"
							className={clsx(styles.tile, styles.tileButton, istAktiv('atCap') && styles.tileActive)}
							onClick={() => kachel('atCap')}
						>
							<span className={styles.tileLabel}>{t('Cap reached')}</span>
							<span className={styles.tileValue}>{k.companiesAtCap ?? 0}</span>
						</button>
						<div className={styles.tile}>
							<span className={styles.tileLabel}>{t('Credit')}</span>
							<span className={styles.tileValue}>{formatEuro(Number(k.creditPending || 0) + Number(k.creditApplied || 0))}</span>
							<span className={styles.tileHint}>
								{t('{{applied}} on invoices · {{pending}} pending', {
									applied: formatEuro(k.creditApplied),
									pending: formatEuro(k.creditPending)
								})}
								{Number(k.creditRemainder) > 0 &&
									` · ${t('{{n}} stays as credit', { n: formatEuro(k.creditRemainder) })}`}
							</span>
						</div>
						<div className={styles.tile}>
							<span className={styles.tileLabel}>{t('Certificates issued')}</span>
							<span className={styles.tileValue}>{k.certificates ?? 0}</span>
						</div>
					</div>

					<p className={styles.note}>
						{t('A click on a row shows the students of the Junior Company with their status per module.')}
					</p>

					<div className="container">
						<Table columns={spalten} items={zeilen} onRowClick={row => setOffen(row)} />
					</div>

					{data.quality && (
						<div className={styles.quality}>
							<h3>{t('Where the course gets stuck')}</h3>
							<div className={styles.qualityCols}>
								<div>
									<h4>{t('Modules')}</h4>
									<table className={styles.simple}>
										<thead>
											<tr>
												<th>{t('Module')}</th>
												<th>{t('Started')}</th>
												<th>{t('Passed')}</th>
												<th>{t('Ø attempts')}</th>
											</tr>
										</thead>
										<tbody>
											{(data.quality.modules || []).map(modul => (
												<tr key={modul.id}>
													<td>{modul.title}</td>
													<td>{modul.started}</td>
													<td>{modul.passed}</td>
													<td>{modul.avgAttempts != null ? Number(modul.avgAttempts).toFixed(1) : '—'}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
								<div>
									<h4>{t('Hardest questions')}</h4>
									<table className={styles.simple}>
										<thead>
											<tr>
												<th>{t('Question')}</th>
												<th>{t('Asked')}</th>
												<th>{t('Wrong')}</th>
											</tr>
										</thead>
										<tbody>
											{(data.quality.questions || []).slice(0, 10).map(frage => (
												<tr key={frage.id}>
													<td>
														{frage.text}
														<br />
														<small>{frage.moduleTitle}</small>
													</td>
													<td>{frage.asked}</td>
													<td>{Math.round((frage.wrongRate || 0) * 100)} %</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					)}
				</>
			)}

			{offen && <CompanyDialog company={offen} courseId={kursId} onClose={() => setOffen(null)} />}

			<FilterPanel
				open={filterOffen}
				onClose={() => setFilterOffen(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={filters.setField}
				onResetAll={filters.resetAll}
			/>
		</div>
	)
}

CourseReport.propTypes = {
	role: PropTypes.string
}

const mapStateToProps = state => ({ role: state.user.role })

export default connect(mapStateToProps)(CourseReport)
